/**
 * Wellnest MD — wellness / aesthetic offerings for provider code `Wellnest1300`.
 * Sourced from "Dr Reddy Treatment Offering Details" CSV + aligned with skin-type-react wellness ids where applicable.
 * Educational / plan-building only — not medical advice.
 */

export const WELLNEST_PROVIDER_CODE = "Wellnest1300";

export type WellnestOffering = {
  /** Value stored as DiscussedItem.treatment (and chapter key) */
  treatmentName: string;
  /** Broad bucket for recommender grouping */
  category: string;
  /** Stable id for Wellnest recommender browse chips (see `WELLNEST_BROWSE_GROUP_LABELS`) */
  browseGroup: string;
  addresses: string;
  demographics: string;
  delivery: string;
  /** Raw pricing string from sheet (display + light parse) */
  pricing: string;
  notes: string;
  resultsTimeline: string;
  /** Optional id aligned with skin-type-react wellnessQuiz ids for asset cross-ref */
  wellnessQuizId?: string;
};

/**
 * Regulatory blurb — show in patient-facing materials only as approved by counsel.
 */
export const WELLNEST_REGULATORY_NOTICE =
  "These compounds are investigational research peptides. They are not FDA-approved for general therapeutic use unless otherwise specified. Content is for scientific and educational discussion only.";

export const WELLNEST_OFFERINGS: WellnestOffering[] = [
  {
    treatmentName: "BPC-157",
    category: "Recovery, inflammation & gut",
    browseGroup: "recovery-tissue",
    addresses:
      "Soft tissue repair support, Tendon/ligament recovery, Chronic GI issues, GI lining support, Anti-inflammatory properties",
    demographics:
      "Adults 30+ with contact sports, intense workouts, or active lifestyle; often 40+ male and female",
    delivery: "SC injection preferred; oral and nasal spray available",
    pricing: "$250",
    notes:
      "5 weeks supply; ready-to-use, prepared under strict aseptic precautions to avoid cross-contamination",
    resultsTimeline: "2–8 weeks",
    wellnessQuizId: "bpc-157",
  },
  {
    treatmentName: "Thymosin Beta-4 (TB-500)",
    category: "Musculoskeletal injury",
    browseGroup: "recovery-tissue",
    addresses:
      "Accelerated muscle recovery, Reduced inflammation, Improved mobility",
    demographics:
      "Adults 30+ with contact sports, intense workouts, or active lifestyle; often 40+",
    delivery: "SC injection preferred; nasal spray available",
    pricing: "$200",
    notes:
      "Ready-to-use, prepared under strict aseptic precautions to avoid cross-contamination",
    resultsTimeline: "1–8 weeks",
    wellnessQuizId: "tb-500",
  },
  {
    treatmentName: "CJC-1295",
    category: "Energy & recovery",
    browseGroup: "gh-performance",
    addresses: "",
    demographics: "Poor muscle mass gain / toning, men and women",
    delivery: "SC injection",
    pricing: "$250",
    notes: "5 weeks minimum",
    resultsTimeline: "4–10 weeks",
    wellnessQuizId: "cjc-1295",
  },
  {
    treatmentName: "Ipamorelin",
    category: "Sleep & muscle",
    browseGroup: "gh-performance",
    addresses:
      "Selective ghrelin receptor agonist, Stimulates natural GH release, Minimal cortisol elevation, Supports lean mass preservation",
    demographics: "Aged 40+, both sexes",
    delivery: "SC injection only",
    pricing: "$250",
    notes: "5 weeks",
    resultsTimeline: "4–10 weeks",
    wellnessQuizId: "ipamorelin",
  },
  {
    treatmentName: "Semax",
    category: "Cognitive",
    browseGroup: "cognition-mood",
    addresses: "Brain fog, focus, cognitive decline",
    demographics: "Aged 30+",
    delivery: "SC injection; nasal spray available",
    pricing: "$300",
    notes: "10 weeks supply",
    resultsTimeline: "8–16 weeks",
    wellnessQuizId: "semax",
  },
  {
    treatmentName: "ARA-290",
    category: "Functional pain & recovery",
    browseGroup: "functional-recovery",
    addresses:
      "Inflammation modulation, nerve discomfort support, functional recovery protocols",
    demographics: "Adults with recovery, pain, or inflammatory concerns; provider discretion",
    delivery: "SC injection",
    pricing: "Custom quote",
    notes:
      "Used as an adjunct in Wellnest arthritis, spine injury, tendon, and ligament injury protocols",
    resultsTimeline: "2–8 weeks",
  },
  {
    treatmentName: "Selank",
    category: "Stress & mood",
    browseGroup: "cognition-mood",
    addresses: "Anxiolytic effects, Improved cognition, Mood balance",
    demographics: "Aged 30+, both sexes",
    delivery: "SC injection ideal",
    pricing: "$300–$500",
    notes: "5–10 weeks",
    resultsTimeline: "6–16 weeks",
    wellnessQuizId: "selank",
  },
  {
    treatmentName: "P-21",
    category: "Cognitive",
    browseGroup: "cognition-mood",
    addresses: "Synapse regeneration",
    demographics: "Aged 60+",
    delivery: "SC injection",
    pricing: "$500",
    notes: "10 weeks",
    resultsTimeline: "3–6 months",
    wellnessQuizId: "p21",
  },
  {
    treatmentName: "Pinealon",
    category: "Cognitive",
    browseGroup: "cognition-mood",
    addresses: "Brain oxidative defence, cognitive decline",
    demographics: "Aged 60+",
    delivery: "SC injection",
    pricing: "$500",
    notes: "10–16 weeks",
    resultsTimeline: "3–6 months",
    wellnessQuizId: "pinealon",
  },
  {
    treatmentName: "GHRP-2 / GHRP-6",
    category: "Muscle & composition",
    browseGroup: "gh-performance",
    addresses: "Recovery, body composition",
    demographics: "Aged 35+",
    delivery: "SC injection",
    pricing: "$250",
    notes: "5 weeks",
    resultsTimeline: "2–5 months",
    wellnessQuizId: "ghrp-2-6",
  },
  {
    treatmentName: "IGF-1 LR3",
    category: "Muscle & composition",
    browseGroup: "gh-performance",
    addresses: "Muscle growth",
    demographics: "Aged 35+",
    delivery: "SC injection",
    pricing: "$250",
    notes: "5 weeks",
    resultsTimeline: "2–5 months",
    wellnessQuizId: "igf-1-lr3",
  },
  {
    treatmentName: "GHK-Cu",
    category: "Skin",
    browseGroup: "skin-aesthetic",
    addresses: "Skin firmness, skin laxity and elastin stimulation",
    demographics: "Aged 40+",
    delivery: "SC injection or topical peptide cream",
    pricing: "$250–$350",
    notes: "5–8 weeks",
    resultsTimeline: "2–3 months",
    wellnessQuizId: "ghk-cu",
  },
  {
    treatmentName: "Proprietary GHK-Cu Peptide Blend",
    category: "Skin",
    browseGroup: "skin-aesthetic",
    addresses:
      "Skin quality, fine lines, laxity, acne support, post-device skin recovery",
    demographics: "Men and women; aesthetic skin-quality protocols",
    delivery: "Topical peptide blend; SC injection option",
    pricing: "Custom quote",
    notes:
      "Used alone or paired with EMface, VST, VFR, IPL, and selected body-contouring protocols",
    resultsTimeline: "2–3 months",
    wellnessQuizId: "ghk-cu",
  },
  {
    treatmentName: "Melanotan 2",
    category: "Skin & tanning",
    browseGroup: "skin-aesthetic",
    addresses: "Melanin increase, libido increase",
    demographics: "Natural tanning peptide pathway; provider discretion",
    delivery: "SC injection",
    pricing: "$200+",
    notes: "5 weeks minimum",
    resultsTimeline: "3+ months",
    wellnessQuizId: "melanotan-2",
  },
  {
    treatmentName: "MK-677",
    category: "Bone & joint",
    browseGroup: "bone-joint",
    addresses: "Bone density decline prevention",
    demographics: "Often 65+",
    delivery: "SC injection",
    pricing: "$350–$600",
    notes: "5–10 weeks",
    resultsTimeline: "3+ months",
    wellnessQuizId: "mk-677",
  },
  {
    treatmentName: "Sermorelin",
    category: "Longevity & GH axis",
    browseGroup: "gh-performance",
    addresses: "Physiologic GH stimulation, Anti-aging interest",
    demographics: "Aged 40+",
    delivery: "SC injection",
    pricing: "$300–$500",
    notes: "5–10 weeks",
    resultsTimeline: "8–12 weeks",
    wellnessQuizId: "sermorelin",
  },
  {
    treatmentName: "Tesamorelin",
    category: "Metabolic & body composition",
    browseGroup: "metabolic-composition",
    addresses: "Obesity adjunct therapy",
    demographics: "Aged 40+",
    delivery: "SC injection",
    pricing: "$500",
    notes: "5–10 weeks",
    resultsTimeline: "3+ months",
    wellnessQuizId: "tessamorelin",
  },
  {
    treatmentName: "Epitalon",
    category: "Longevity",
    browseGroup: "longevity",
    addresses: "Metabolism reset",
    demographics: "Aged 40+",
    delivery: "SC injection",
    pricing: "$400–$500",
    notes: "5–10 weeks",
    resultsTimeline: "3+ months",
    wellnessQuizId: "epitalon",
  },
  {
    treatmentName: "AOD-9604",
    category: "Metabolic & body composition",
    browseGroup: "metabolic-composition",
    addresses: "Obesity adjunct therapy",
    demographics: "Aged 30+, both sexes",
    delivery: "SC injection",
    pricing: "$300–$500",
    notes: "1–2 months",
    resultsTimeline: "3+ months",
    wellnessQuizId: "aod-9604",
  },
  {
    treatmentName: "GLP-1",
    category: "Metabolic & body composition",
    browseGroup: "metabolic-composition",
    addresses: "Metabolic and weight-management support",
    demographics: "Adults seeking metabolic support; provider discretion",
    delivery: "Injection protocol",
    pricing: "Custom quote",
    notes:
      "Used as an adjunct in Wellnest body-composition and OSA support protocols when clinically appropriate",
    resultsTimeline: "1–6 months",
  },
  {
    treatmentName: "Cartalax",
    category: "Bone & joint",
    browseGroup: "bone-joint",
    addresses: "Cartilage repair",
    demographics: "Aged 50+",
    delivery: "SC injection",
    pricing: "$350–$500",
    notes: "5–10 weeks",
    resultsTimeline: "3+ months",
    wellnessQuizId: "cartalax",
  },
  {
    treatmentName: "EMface",
    category: "Face & skin tightening",
    browseGroup: "face-aesthetic",
    addresses:
      "Forehead wrinkles, bunny lines, nasolabial folds, marionette lines, jowls, needle-free facial lifting",
    demographics: "Men and women seeking needle-free facial muscle toning and lift",
    delivery: "Forehead; lower face; jawline; custom face protocol",
    pricing: "Custom quote",
    notes:
      "Used as Wellnest's chemical-free, needle-free alternative for dynamic facial wrinkles and lift protocols",
    resultsTimeline: "4–12 weeks",
  },
  {
    treatmentName: "EMface Eyes",
    category: "Face & skin tightening",
    browseGroup: "face-aesthetic",
    addresses: "Tear troughs, under-eye hollowing, tired-looking eyes",
    demographics: "Men and women seeking non-injectable eye-area support",
    delivery: "Eye-area protocol; paired VST/VFR protocol",
    pricing: "Custom quote",
    notes:
      "Often paired with VST, VFR, and proprietary GHK-Cu peptide blend for tear-trough concerns",
    resultsTimeline: "4–12 weeks",
  },
  {
    treatmentName: "EMface Chin",
    category: "Face & skin tightening",
    browseGroup: "face-aesthetic",
    addresses: "Submental fullness and OSA-adjacent chin/neck protocol support",
    demographics: "Adults with chin/submental laxity concerns; provider discretion for OSA support",
    delivery: "Chin protocol; paired VForm protocol",
    pricing: "Custom quote",
    notes:
      "For obstructive sleep apnea support workflows, paired with VForm and metabolic peptides when appropriate",
    resultsTimeline: "4–12 weeks",
  },
  {
    treatmentName: "EMface TMJ",
    category: "Functional pain & recovery",
    browseGroup: "functional-recovery",
    addresses: "TMJ pain and jaw tension protocols",
    demographics: "Adults with TMJ pain or jaw-muscle tension concerns",
    delivery: "TMJ protocol; paired Thymosin protocol",
    pricing: "Custom quote",
    notes: "May be paired with Thymosin peptide therapy per provider protocol",
    resultsTimeline: "2–8 weeks",
  },
  {
    treatmentName: "VST Skin Tightening",
    category: "Face & skin tightening",
    browseGroup: "face-aesthetic",
    addresses:
      "Crow's feet, bunny lines, nasolabial folds, marionette lines, barcode lines, jowls, tear troughs, skin laxity",
    demographics: "Men and women seeking radiofrequency skin tightening",
    delivery: "Face; eyes; upper lip; jawline; neck; custom area",
    pricing: "Custom quote",
    notes:
      "Sinclair V20 radiofrequency heat-based skin-tightening protocol; may pair with proprietary GHK-Cu peptide blend",
    resultsTimeline: "4–12 weeks",
  },
  {
    treatmentName: "VFR Fractional Rejuvenation",
    category: "Face & skin tightening",
    browseGroup: "face-aesthetic",
    addresses:
      "Crow's feet, turkey neck, tear troughs, texture, fractional skin rejuvenation",
    demographics: "Men and women seeking fractional radiofrequency rejuvenation",
    delivery: "Eyes; neck; face; custom area",
    pricing: "Custom quote",
    notes:
      "Fractional rejuvenation using radiofrequency heat; may pair with proprietary GHK-Cu peptide blend",
    resultsTimeline: "4–12 weeks",
  },
  {
    treatmentName: "IPL",
    category: "Skin & hair removal",
    browseGroup: "skin-aesthetic",
    addresses:
      "Hair removal, back acne, acne, melasma, pigmentation lesions, skin tone",
    demographics: "Men and women, all skin types; provider evaluates settings and candidacy",
    delivery: "Hair removal; acne/back acne; melasma; pigmentation lesions",
    pricing: "Custom quote",
    notes:
      "May be categorized as laser/light therapy; acne protocols may pair with proprietary skin peptide blend",
    resultsTimeline: "2–12 weeks",
  },
  {
    treatmentName: "EMsculpt Neo",
    category: "Body contouring",
    browseGroup: "body-contouring",
    addresses: "Body contouring for arms, thighs, abdomen, buttocks",
    demographics: "Men and women seeking muscle toning and body composition support",
    delivery: "Arms; thighs; abdomen; buttocks",
    pricing: "Custom quote",
    notes:
      "BTL EMS-based body-contouring technology; may pair with metabolic/recovery peptides",
    resultsTimeline: "4–12 weeks",
  },
  {
    treatmentName: "VForm",
    category: "Body contouring",
    browseGroup: "body-contouring",
    addresses:
      "Body contouring for thighs, abdomen, buttocks; chin/neck support protocols",
    demographics: "Men and women seeking radiofrequency body contouring",
    delivery: "Thighs; abdomen; buttocks; chin/neck",
    pricing: "Custom quote",
    notes:
      "Sinclair V20 radiofrequency heat-based body-contouring technology",
    resultsTimeline: "4–12 weeks",
  },
  {
    treatmentName: "EMtone",
    category: "Body contouring",
    browseGroup: "body-contouring",
    addresses: "Buttocks contouring and skin texture support",
    demographics: "Men and women seeking buttocks contouring or toning",
    delivery: "Buttocks",
    pricing: "Custom quote",
    notes: "Used in Wellnest buttocks contouring protocols",
    resultsTimeline: "4–12 weeks",
  },
  {
    treatmentName: "EMsculpt Functional Protocols",
    category: "Functional pain & recovery",
    browseGroup: "functional-recovery",
    addresses:
      "Arthritis, spine injuries, tendon injuries, ligament injuries, functional rehabilitation support",
    demographics: "Adults with musculoskeletal pain, injury, or recovery goals",
    delivery: "Arthritis protocol; spine protocol; tendon/ligament protocol",
    pricing: "Custom quote",
    notes:
      "May pair with BPC-157, TB-500, ARA-290, and Semax per provider protocol",
    resultsTimeline: "2–12 weeks",
  },
];

/** Display order for Wellnest peptide “focus” browse chips in the treatment recommender. */
export const WELLNEST_BROWSE_GROUP_ORDER = [
  "recovery-tissue",
  "gh-performance",
  "cognition-mood",
  "metabolic-composition",
  "skin-aesthetic",
  "face-aesthetic",
  "body-contouring",
  "functional-recovery",
  "bone-joint",
  "longevity",
] as const;

export const WELLNEST_BROWSE_GROUP_LABELS: Record<string, string> = {
  "recovery-tissue": "Recovery & tissue",
  "gh-performance": "GH · performance · sleep",
  "cognition-mood": "Cognition & mood",
  "metabolic-composition": "Metabolic & composition",
  "skin-aesthetic": "Skin & tanning",
  "face-aesthetic": "Face lift & tightening",
  "body-contouring": "Body contouring",
  "functional-recovery": "Functional recovery",
  "bone-joint": "Bone & joint",
  longevity: "Longevity",
};

export const WELLNEST_BROWSE_GROUP_DESCRIPTIONS: Record<string, string> = {
  "recovery-tissue":
    "Peptides that support soft-tissue repair, tendon and ligament healing, and reduced inflammation. BPC-157 also has GI lining support properties. Relevant for active injuries, post-surgical recovery, or chronic inflammatory conditions.",
  "gh-performance":
    "Growth hormone secretagogues that stimulate natural GH release from the pituitary. Benefits include improved body composition, lean muscle preservation, faster recovery, deeper sleep, and anti-aging effects. Most relevant for adults 40+ experiencing age-related GH decline.",
  "cognition-mood":
    "Peptides targeting brain function and emotional regulation. Semax addresses brain fog and cognitive performance; Selank has anxiolytic and mood-balancing effects; P-21 and Pinealon support synapse health and are typically used in older patients for cognitive protection.",
  "metabolic-composition":
    "Peptides that support fat metabolism and body composition. Tesamorelin and AOD-9604 promote fat loss — particularly visceral adipose tissue — and are used as adjuncts to diet and exercise.",
  "skin-aesthetic":
    "Peptides targeting skin quality and appearance. GHK-Cu stimulates collagen and elastin production, improving firmness and reducing laxity. Melanotan 2 increases melanin for natural tanning support and may also support libido.",
  "face-aesthetic":
    "Needle-free and energy-based facial protocols for lift, dynamic wrinkles, skin tightening, fractional rejuvenation, and eye-area support, including EMface, VST, and VFR.",
  "body-contouring":
    "Device-based body-contouring protocols, including EMsculpt Neo, VForm, and EMtone, with peptide adjuncts when clinically appropriate.",
  "functional-recovery":
    "Functional protocols for musculoskeletal recovery, TMJ support, spine and tendon/ligament injuries, often paired with recovery peptides.",
  "bone-joint":
    "Compounds supporting skeletal and joint health. MK-677 supports bone density and is relevant for osteoporosis risk. Cartalax supports cartilage repair and is used for joint degeneration or arthritis.",
  longevity:
    "Compounds associated with healthy aging and cellular longevity. Epitalon is a tetrapeptide linked to telomere regulation and metabolism reset, used by patients with a focus on extending healthspan.",
};

export type WellnestPriceBand = "budget" | "mid" | "premium" | "variable";

/** Bucket list pricing for filter chips (uses highest number in the price string). */
export function getWellnestPriceBand(
  pricing: string | null | undefined,
): WellnestPriceBand {
  const s = (pricing ?? "").replace(/,/g, "");
  const nums = s.match(/\d+/g)?.map(Number) ?? [];
  if (nums.length === 0) return "variable";
  const hi = Math.max(...nums);
  if (hi >= 500) return "premium";
  if (hi >= 300) return "mid";
  return "budget";
}

export function wellnestDeliveryHasNasal(delivery: string): boolean {
  return /\bnasal\b/i.test(delivery);
}

export function wellnestDeliveryHasOral(delivery: string): boolean {
  return /\boral\b/i.test(delivery);
}

export function wellnestDeliveryHasTopical(delivery: string): boolean {
  return /\btopical\b/i.test(delivery) || /\bcream\b/i.test(delivery);
}

/**
 * Split Dr Reddy sheet column "What It Addresses" (comma-separated phrases) into chip labels.
 */
export function parseWellnestWhatItAddressesChips(
  raw: string | null | undefined,
): string[] {
  if (raw == null || !String(raw).trim()) return [];
  const parts = String(raw)
    .split(",")
    .map((s) => s.trim().replace(/\s+/g, " "))
    .filter((s) => s.length > 0);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of parts) {
    const k = p.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(p);
  }
  return out;
}

const offeringByName = new Map(
  WELLNEST_OFFERINGS.map((o) => [normalizeWellnestTreatmentKey(o.treatmentName), o]),
);

function normalizeWellnestTreatmentKey(name: string): string {
  return name.trim().toLowerCase();
}

export function isWellnestWellnessProviderCode(
  code: string | null | undefined,
): boolean {
  const c = (code ?? "").trim().toLowerCase();
  return c === WELLNEST_PROVIDER_CODE.toLowerCase() || c.startsWith("wellnest");
}

export function getWellnestOfferingByTreatmentName(
  treatment: string | null | undefined,
): WellnestOffering | undefined {
  if (!treatment?.trim()) return undefined;
  return offeringByName.get(normalizeWellnestTreatmentKey(treatment));
}

/** Top-level treatment picker options for recommender / plan (one row per offering). */
export function getWellnestTreatmentOptionNames(): string[] {
  return WELLNEST_OFFERINGS.map((o) => o.treatmentName);
}

type WellnestPlanBuilderUseCase = {
  /** Search terms from assessment findings, concerns, and free-text goals. */
  keywords: string[];
  /** Manual finding labels shown on Wellnest treatment cards. */
  findings: string[];
  area: string;
  treatments: string[];
};

const WELLNEST_PLAN_BUILDER_USE_CASES: WellnestPlanBuilderUseCase[] = [
  {
    keywords: ["forehead wrinkle", "forehead wrinkles"],
    findings: ["Forehead Wrinkles"],
    area: "Forehead",
    treatments: ["EMface", "Proprietary GHK-Cu Peptide Blend"],
  },
  {
    keywords: ["crow's feet", "crows feet", "crow feet", "outer corner"],
    findings: ["Crow's feet"],
    area: "Eyes",
    treatments: [
      "VST Skin Tightening",
      "VFR Fractional Rejuvenation",
      "Proprietary GHK-Cu Peptide Blend",
    ],
  },
  {
    keywords: ["bunny line", "bunny lines", "nose scrunch"],
    findings: ["Bunny Lines"],
    area: "Nose",
    treatments: ["EMface", "VST Skin Tightening", "Proprietary GHK-Cu Peptide Blend"],
  },
  {
    keywords: [
      "parentheses",
      "parenthesis",
      "smile line",
      "smile lines",
      "nasolabial",
    ],
    findings: ["Nasolabial Folds"],
    area: "Nasolabial",
    treatments: [
      "EMface",
      "VST Skin Tightening",
      "Proprietary GHK-Cu Peptide Blend",
      "GHK-Cu",
    ],
  },
  {
    keywords: ["marionette", "puppet"],
    findings: ["Marionette Lines"],
    area: "Marionette lines",
    treatments: ["EMface", "VST Skin Tightening", "Proprietary GHK-Cu Peptide Blend"],
  },
  {
    keywords: [
      "barcode",
      "smoker",
      "smoker's",
      "upper lip line",
      "upper lip lines",
      "perioral",
    ],
    findings: ["Barcode Lines"],
    area: "Lips",
    treatments: ["VST Skin Tightening", "Proprietary GHK-Cu Peptide Blend"],
  },
  {
    keywords: ["turkey neck", "loose neck", "platysmal", "neck band"],
    findings: ["Turkey Neck", "Loose Neck Skin", "Platysmal Bands"],
    area: "Neck",
    treatments: [
      "VFR Fractional Rejuvenation",
      "VST Skin Tightening",
      "Proprietary GHK-Cu Peptide Blend",
    ],
  },
  {
    keywords: ["jowl", "jowls", "jawline definition"],
    findings: ["Jowls", "Ill-Defined Jawline"],
    area: "Jawline",
    treatments: ["EMface", "VST Skin Tightening", "Proprietary GHK-Cu Peptide Blend"],
  },
  {
    keywords: [
      "tear trough",
      "tear troughs",
      "under eye hollow",
      "under eye hollows",
      "under eye",
      "tired eyes",
    ],
    findings: ["Tear Troughs", "Under Eye Hollows", "Eyelid Bags"],
    area: "Under eyes",
    treatments: [
      "EMface Eyes",
      "VST Skin Tightening",
      "VFR Fractional Rejuvenation",
      "Proprietary GHK-Cu Peptide Blend",
    ],
  },
  {
    keywords: ["hair removal"],
    findings: ["Hair Removal"],
    area: "Body",
    treatments: ["IPL"],
  },
  {
    keywords: ["backne", "back acne", "acne", "breakout"],
    findings: ["Back Acne", "Acne"],
    area: "Skin",
    treatments: ["IPL", "Proprietary GHK-Cu Peptide Blend"],
  },
  {
    keywords: ["melasma", "pigmentation lesion", "pigmentation lesions", "pigment"],
    findings: ["Melasma", "Pigmentation Lesions", "Dark Spots"],
    area: "Skin",
    treatments: ["IPL"],
  },
  {
    keywords: ["body contour", "body contouring"],
    findings: ["Body Contouring"],
    area: "Body",
    treatments: [
      "EMsculpt Neo",
      "VForm",
      "EMtone",
      "Tesamorelin",
      "Ipamorelin",
      "IGF-1 LR3",
      "AOD-9604",
      "BPC-157",
      "Thymosin Beta-4 (TB-500)",
    ],
  },
  {
    keywords: ["arm", "arms"],
    findings: ["Body Contouring - Arms"],
    area: "Body",
    treatments: ["EMsculpt Neo"],
  },
  {
    keywords: ["thigh", "thighs"],
    findings: ["Body Contouring - Thighs"],
    area: "Body",
    treatments: ["EMsculpt Neo", "VForm"],
  },
  {
    keywords: ["abdomen", "abdominal", "stomach", "belly"],
    findings: ["Body Contouring - Abdomen"],
    area: "Body",
    treatments: ["EMsculpt Neo", "VForm"],
  },
  {
    keywords: ["buttock", "buttocks", "glute", "glutes"],
    findings: ["Body Contouring - Buttocks"],
    area: "Body",
    treatments: ["EMtone", "EMsculpt Neo", "VForm"],
  },
  {
    keywords: ["arthritis", "spine", "tendon", "ligament", "injury", "injuries"],
    findings: ["Arthritis", "Spine Injuries", "Tendon/Ligament Injuries"],
    area: "Functional",
    treatments: [
      "EMsculpt Functional Protocols",
      "BPC-157",
      "Thymosin Beta-4 (TB-500)",
      "ARA-290",
      "Semax",
    ],
  },
  {
    keywords: ["osa", "obstructive sleep apnea", "sleep apnea"],
    findings: ["Obstructive Sleep Apnea"],
    area: "Functional",
    treatments: ["EMface Chin", "VForm", "Tesamorelin", "GLP-1", "AOD-9604"],
  },
  {
    keywords: ["tmj", "jaw pain"],
    findings: ["TMJ Pain"],
    area: "Functional",
    treatments: ["EMface TMJ", "Thymosin Beta-4 (TB-500)"],
  },
];

function uniqueWellnestTreatments(treatments: string[]): string[] {
  const available = new Set(getWellnestTreatmentOptionNames());
  const seen = new Set<string>();
  const out: string[] = [];
  for (const treatment of treatments) {
    if (!available.has(treatment) || seen.has(treatment)) continue;
    seen.add(treatment);
    out.push(treatment);
  }
  return out;
}

export function getWellnestTreatmentsForInterest(
  interest: string | null | undefined,
): string[] {
  const lower = (interest ?? "").toLowerCase();
  if (!lower.trim()) return getWellnestTreatmentOptionNames();
  const matches = WELLNEST_PLAN_BUILDER_USE_CASES.filter((useCase) =>
    useCase.keywords.some((keyword) => lower.includes(keyword)),
  );
  if (matches.length > 0) {
    const hasSpecificBodyRegion = matches.some((useCase) =>
      useCase.findings.some((finding) => finding.startsWith("Body Contouring -")),
    );
    const effectiveMatches = hasSpecificBodyRegion
      ? matches.filter(
          (useCase) =>
            !(
              useCase.findings.length === 1 &&
              useCase.findings[0] === "Body Contouring"
            ),
        )
      : matches;
    return uniqueWellnestTreatments(
      effectiveMatches.flatMap((useCase) => useCase.treatments),
    );
  }
  if (/fine line|wrinkle|smooth/.test(lower)) {
    return uniqueWellnestTreatments([
      "EMface",
      "VST Skin Tightening",
      "VFR Fractional Rejuvenation",
      "Proprietary GHK-Cu Peptide Blend",
    ]);
  }
  if (/skin tone|even skin|scar|exfoliate|texture|pore/.test(lower)) {
    return uniqueWellnestTreatments([
      "IPL",
      "VFR Fractional Rejuvenation",
      "Proprietary GHK-Cu Peptide Blend",
    ]);
  }
  if (/laxity|tighten|sag|lift/.test(lower)) {
    return uniqueWellnestTreatments([
      "EMface",
      "VST Skin Tightening",
      "VFR Fractional Rejuvenation",
      "Proprietary GHK-Cu Peptide Blend",
    ]);
  }
  return getWellnestTreatmentOptionNames();
}

export function getWellnestFindingsByAreaForTreatment(
  treatment: string | null | undefined,
): { area: string; findings: string[] }[] {
  const treatmentName = treatment?.trim();
  if (!treatmentName || !getWellnestOfferingByTreatmentName(treatmentName)) {
    return [];
  }
  const byArea = new Map<string, string[]>();
  for (const useCase of WELLNEST_PLAN_BUILDER_USE_CASES) {
    if (!useCase.treatments.includes(treatmentName)) continue;
    const list = byArea.get(useCase.area) ?? [];
    for (const finding of useCase.findings) {
      if (!list.includes(finding)) list.push(finding);
    }
    byArea.set(useCase.area, list);
  }
  return Array.from(byArea.entries()).map(([area, findings]) => ({
    area,
    findings,
  }));
}

/**
 * Product / delivery line items for the second dropdown (optional detail).
 */
export function getWellnestProductOptionsForTreatment(
  treatment: string | undefined,
): string[] {
  const o = getWellnestOfferingByTreatmentName(treatment);
  if (!o) return [];
  const parts = o.delivery
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);
  const base = parts.length ? parts : [o.delivery.trim()].filter(Boolean);
  return [...base, "Other"];
}

/**
 * True when `product` is only a delivery-form line from the Wellnest offering sheet
 * (the second dropdown: e.g. "Nasal spray available", "SC injection", "Other") — not a
 * distinct product name. Plan and checkout labels should lead with the peptide
 * {@link treatmentName} instead of replacing the title with this string.
 */
export function isWellnestDeliveryFormProductLine(
  treatment: string | undefined,
  product: string | undefined,
): boolean {
  const t = treatment?.trim();
  const p = product?.trim();
  if (!t || !p) return false;
  if (!getWellnestOfferingByTreatmentName(t)) return false;
  const pLower = p.toLowerCase();
  return getWellnestProductOptionsForTreatment(t).some(
    (opt) => opt.trim().toLowerCase() === pLower,
  );
}

function parseWellnestSummaryNote(note: string): {
  notes?: string;
  protocolLength?: string;
} {
  const trimmed = note.trim();
  if (!trimmed) return {};
  const isDurationOnly =
    trimmed.length <= 40 &&
    /\b(?:week|weeks|month|months)\b/i.test(trimmed) &&
    !/[.;,]/.test(trimmed);
  return isDurationOnly ? { protocolLength: trimmed } : { notes: trimmed };
}

/** Meta for blueprint chapters + checkout fallbacks */
export function getWellnestPeptideMeta(treatment: string): {
  longevity?: string;
  longevityFactLabel?: string;
  downtime?: string;
  downtimeFactLabel?: string;
  protocolLength?: string;
  protocolLengthFactLabel?: string;
  notes?: string;
  priceRange?: string;
} | null {
  const o = getWellnestOfferingByTreatmentName(treatment);
  if (!o) return null;
  const summary = parseWellnestSummaryNote(o.notes);
  return {
    longevity: o.resultsTimeline,
    longevityFactLabel: "Results",
    downtime: o.delivery,
    downtimeFactLabel: "Delivery",
    protocolLength: summary.protocolLength,
    protocolLengthFactLabel: summary.protocolLength ? "Protocol" : undefined,
    notes: summary.notes,
    priceRange: o.pricing,
  };
}

/** Parse a conservative numeric price for checkout totals (min of range). */
export function parseWellnestPriceMin(pricing: string): number {
  const s = pricing.replace(/\s/g, "");
  const matches = s.match(/\$[\d,]+(?:\.\d+)?/g);
  if (!matches?.length) return 0;
  const nums = matches.map((m) => {
    const n = parseFloat(m.replace(/[$,]/g, ""));
    return Number.isFinite(n) ? n : 0;
  });
  const positives = nums.filter((n) => n > 0);
  return positives.length ? Math.min(...positives) : 0;
}
